// Layout-Spezifikation (Pflichtenheft Kap. 6.2) als Zod-Schema.
// specVersion 1: feste Zonen in Pixeln der Referenzauflösung (MVP).
// specVersion 2: frei positionierte Elemente in Prozent (Designer aus dem Prototyp, Kap. 15.3).
import { z } from 'zod';
import { CLOCK_FACES } from './analog.js';
import { HIGH_LAT_RULES, IQAMA_RE, JUMUAH_RE, PRAYER_LANGS, PRAYER_METHODS, PRAYER_SOURCES, PRAYER_VIEWS } from './prayer.js';
import { DataBindingSchema } from './data.js';
const sha256 = z.string().regex(/^[0-9a-f]{64}$/);
const color = z.string().max(64);
export const FitSchema = z.enum(['cover', 'contain', 'fill']);
/** Nur http(s) – leere Eingabe bzw. die Vorgabe "https://" bleiben erlaubt (Platzhalter). */
export const HTTP_URL_RE = /^https?:\/\//i;
/** Wie im Prototyp: Adresse gilt erst als gesetzt, wenn nach dem Schema etwas folgt. */
export const HTTP_URL_COMPLETE_RE = /^https?:\/\/./i;
const httpUrl = z.string().refine((u) => u === '' || HTTP_URL_RE.test(u), 'Nur http(s)-Adressen erlaubt');
// Kamera-Stream: dieselben Protokolle, die der Player über go2rtc annimmt (UniFi, Hikvision, Dahua, ONVIF, HLS …).
const rtspUrl = z.string().refine((u) => u === '' || /^(rtsps?|rtspx|rtmps?|onvif|https?):\/\//i.test(u), 'Nur Kamera-Adressen (rtsp, rtsps, onvif, rtmp, http, https) erlaubt');
/** Medienquelle im Designer (URL/Pfad); keine Skript-Schemata. */
const mediaSrc = z.string().refine((u) => !/^\s*(javascript|vbscript):/i.test(u), 'Ungültige Medienadresse');
// ---------- specVersion 1 (Zonen) ----------
export const TextStyleSchema = z.object({
    font: z.string().optional(),
    weight: z.number().int().min(100).max(900).optional(),
    /** Pixel relativ zu refHeight. */
    size: z.number().positive().optional(),
    color: color.optional(),
    align: z.enum(['left', 'center', 'right']).optional(),
    maxLines: z.number().int().positive().optional(),
    autoShrink: z.boolean().optional(),
});
export const ClockStyleSchema = z.object({
    format: z.string().max(64).optional(),
    color: color.optional(),
    size: z.number().positive().optional(),
    font: z.string().optional(),
    weight: z.number().int().min(100).max(900).optional(),
    align: z.enum(['left', 'center', 'right']).optional(),
});
const zoneBase = {
    id: z.string().min(1).max(64),
    x: z.number(),
    y: z.number(),
    w: z.number().positive(),
    h: z.number().positive(),
    z: z.number().int().default(1),
};
export const MediaZoneSchema = z.object({
    ...zoneBase,
    type: z.literal('media'),
    fit: FitSchema.default('cover'),
    /** "playlist" = Unter-Playlist (Seitenleiste mit wechselnden Bildern, SOLL). */
    source: z.enum(['single', 'playlist']).optional(),
});
export const TextZoneSchema = z.object({ ...zoneBase, type: z.literal('text'), style: TextStyleSchema.default({}) });
export const LogoZoneSchema = z.object({ ...zoneBase, type: z.literal('logo'), fit: FitSchema.default('contain') });
export const ClockZoneSchema = z.object({ ...zoneBase, type: z.literal('clock'), style: ClockStyleSchema.default({}) });
/** Unbekannte Zonentypen bleiben lesbar und werden leer gerendert (Erweiterbarkeit ohne Formatbruch). */
export const UnknownZoneSchema = z.looseObject({ ...zoneBase, type: z.string() });
export const ZoneSchema = z.union([MediaZoneSchema, TextZoneSchema, LogoZoneSchema, ClockZoneSchema, UnknownZoneSchema]);
export const FieldSchema = z.object({
    zone: z.string(),
    label: z.object({ de: z.string(), en: z.string() }),
    required: z.boolean().optional(),
    maxLength: z.number().int().positive().optional(),
});
export const LayoutV1Schema = z.object({
    specVersion: z.literal(1),
    refWidth: z.number().int().positive(),
    refHeight: z.number().int().positive(),
    orientation: z.enum(['landscape', 'portrait']).optional(),
    background: z.object({ color: color.default('#000000'), asset: sha256.nullish() }).default({ color: '#000000' }),
    fonts: z.array(z.string()).default([]),
    zones: z.array(ZoneSchema),
    fields: z.array(FieldSchema).default([]),
});
/** Zoneninhalt eines Playlist-Eintrags (Kap. 6.3), gleiche Form wie im Manifest. */
export const ZoneContentSchema = z.union([
    z.object({ type: z.literal('text'), value: z.string() }),
    z.object({ type: z.literal('media'), asset: sha256, fit: FitSchema.optional() }),
    z.object({
        type: z.literal('playlist'),
        items: z.array(z.object({ asset: sha256, durationMs: z.number().int().positive().optional() })),
        fit: FitSchema.optional(),
    }),
    // Feldbelegung aus einer Datenquelle (nur im Playlist-Eintrag; der Server macht daraus Text fürs Manifest)
    DataBindingSchema,
]);
// ---------- specVersion 2 (freie Elemente, Prototyp) ----------
const elBase = {
    id: z.string().optional(),
    /** Position und Grösse in Prozent der Folie. */
    x: z.number(),
    y: z.number(),
    w: z.number(),
    h: z.number(),
    bg: color.optional(),
    /** Eckenradius in cqh. */
    radius: z.number().optional(),
};
const textish = {
    size: z.number().optional(),
    color: color.optional(),
    align: z.enum(['left', 'center', 'right']).optional(),
    bold: z.boolean().optional(),
};
/** Schriftfamilien aus fester Liste (DQ-STU-003), jeweils mit Systemschriften als Ausweichlösung. */
export const FONT_FAMILIES = ['sans', 'display', 'serif', 'condensed', 'mono'];
export const FontFamilySchema = z.enum(FONT_FAMILIES);
/** Feinere Typografie für Text und Laufschrift (Branchenvorlagen); alles optional, ohne Angabe wie bisher. */
const typo = {
    font: FontFamilySchema.optional(),
    /** Schriftstärke 100–900, überschreibt bold. */
    weight: z.number().int().min(100).max(900).optional(),
    italic: z.boolean().optional(),
    /** Zeilenhöhe als Faktor. */
    lineHeight: z.number().min(0.6).max(3).optional(),
    /** Zeichenabstand in em. */
    spacing: z.number().min(-0.2).max(1).optional(),
    upper: z.boolean().optional(),
    valign: z.enum(['top', 'center', 'bottom']).optional(),
    /** Innenabstand in cqh. */
    pad: z.number().min(0).max(20).optional(),
};
export const TextElementSchema = z.object({ ...elBase, ...textish, ...typo, type: z.literal('text'), text: z.string().default('') });
export const ImageElementSchema = z.object({
    ...elBase,
    type: z.literal('image'),
    src: mediaSrc.optional(),
    asset: sha256.optional(),
    fit: FitSchema.optional(),
});
export const SlideshowElementSchema = z.object({
    ...elBase,
    type: z.literal('slideshow'),
    srcs: z.array(mediaSrc).optional(),
    assets: z.array(sha256).optional(),
    /** Sekunden, mindestens 2. */
    interval: z.number().optional(),
    fit: FitSchema.optional(),
});
export const VideoElementSchema = z.object({
    ...elBase,
    type: z.literal('video'),
    src: mediaSrc.optional(),
    asset: sha256.optional(),
    fit: FitSchema.optional(),
});
export const ClockElementSchema = z.object({
    ...elBase,
    ...textish,
    type: z.literal('clock'),
    /** Fehlt = digital (ältere Folien bleiben unverändert). */
    face: z.enum(CLOCK_FACES).optional(),
    /** both = Uhrzeit und Datum, time = nur Uhrzeit, date = nur Datum (nur digital). */
    mode: z.enum(['both', 'time', 'date']).optional(),
    /** Digital: Sekunden zeigen. */
    seconds: z.boolean().optional(),
    /** Analog: Sekundenzeiger (fehlt = an). */
    secondHand: z.boolean().optional(),
    /** Analog: Zifferblatt-, Zeiger- und Sekundenzeigerfarbe (fehlt = Vorgabe des Zifferblatts). */
    faceColor: color.optional(),
    handColor: color.optional(),
    accent: color.optional(),
    /** Analog: Beschriftung unter der Uhr, z. B. Ort. */
    caption: z.string().max(80).optional(),
    /** IANA-Zeitzone, z. B. "Europe/Vienna"; leer = Zeitzone des Bildschirms. */
    timeZone: z.string().max(64).regex(/^([A-Za-z][A-Za-z0-9_+-]*(\/[A-Za-z0-9_+-]+)*)?$/, 'Ungültige Zeitzone').optional(),
});
export const WeatherElementSchema = z.object({
    ...elBase,
    ...textish,
    type: z.literal('weather'),
    place: z.string().optional(),
    lat: z.number().min(-90).max(90).nullable().optional(),
    lon: z.number().min(-180).max(180).nullable().optional(),
    days: z.number().int().min(0).max(7).optional(),
});
export const WebElementSchema = z.object({
    ...elBase,
    type: z.literal('web'),
    url: httpUrl.default(''),
    /** Minuten, 0 = nie. */
    reload: z.number().optional(),
    zoom: z.number().optional(),
});
export const CameraElementSchema = z.object({
    ...elBase,
    type: z.literal('camera'),
    source: z.enum(['url', 'device', 'rtsp']).optional(),
    url: httpUrl.optional(),
    /** Sekunden. */
    refresh: z.number().optional(),
    fit: FitSchema.optional(),
    rtspUrl: rtspUrl.optional(),
    name: z.string().optional(),
});
export const TickerElementSchema = z.object({
    ...elBase,
    ...textish,
    ...typo,
    type: z.literal('ticker'),
    text: z.string().default(''),
    /** Sekunden pro Durchlauf, mindestens 5. */
    speed: z.number().optional(),
});
export const ScheduleRowSchema = z.object({
    time: z.string().optional(),
    course: z.string().optional(),
    trainer: z.string().optional(),
    room: z.string().optional(),
});
export const ScheduleElementSchema = z.object({
    ...elBase,
    type: z.literal('schedule'),
    title: z.string().optional(),
    size: z.number().optional(),
    color: color.optional(),
    /** Farbe für Überschrift und Uhrzeiten (Standard: Gelb wie im Prototyp). */
    accent: color.optional(),
    font: FontFamilySchema.optional(),
    rows: z.array(ScheduleRowSchema).optional(),
});
const iqamaSpec = z.string().max(6).regex(IQAMA_RE, 'Iqama als +Minuten oder HH:mm').optional();
const minutes = z.number().int().min(-60).max(60).optional();
/** Gebetszeiten (Erweiterung für Moscheen/Vereine): Berechnung im Player, eigener Zeitplan oder Anbieter (vaktija.ba). */
export const PrayerElementSchema = z.object({
    ...elBase,
    ...textish,
    type: z.literal('prayer'),
    /** calc = Berechnung offline im Player, table = hochgeladener Zeitplan, provider = Anbieter (z. B. vaktija.ba). */
    source: z.enum(PRAYER_SOURCES).optional(),
    method: z.enum(PRAYER_METHODS).optional(),
    fajrAngle: z.number().min(1).max(30).optional(),
    ishaAngle: z.number().min(1).max(30).optional(),
    ishaInterval: z.number().int().min(0).max(180).optional(),
    asr: z.enum(['shafi', 'hanafi']).optional(),
    highLat: z.enum(HIGH_LAT_RULES).optional(),
    place: z.string().max(80).optional(),
    lat: z.number().min(-90).max(90).nullable().optional(),
    lon: z.number().min(-180).max(180).nullable().optional(),
    /** IANA-Zeitzone; leer = Zeitzone des Standorts. */
    timeZone: z.string().max(64).regex(/^([A-Za-z][A-Za-z0-9_+-]*(\/[A-Za-z0-9_+-]+)*)?$/, 'Ungültige Zeitzone').optional(),
    /** Minutenkorrektur je Gebet (−60 … +60). */
    adjust: z.object({ fajr: minutes, sunrise: minutes, dhuhr: minutes, asr: minutes, maghrib: minutes, isha: minutes }).optional(),
    /** Hochgeladener Zeitplan (id). */
    table: z.string().max(64).regex(/^[0-9a-f-]*$/i, 'Ungültiger Zeitplan').optional(),
    /** Anbieter (z. B. "vaktija-ba") und Ort beim Anbieter. */
    provider: z.string().max(32).regex(/^[a-z0-9-]*$/, 'Ungültiger Anbieter').optional(),
    location: z.string().max(40).regex(/^[A-Za-z0-9_-]*$/, 'Ungültiger Ort').optional(),
    iqama: z.object({ fajr: iqamaSpec, dhuhr: iqamaSpec, asr: iqamaSpec, maghrib: iqamaSpec, isha: iqamaSpec }).optional(),
    jumuah: z.string().max(40).regex(JUMUAH_RE, 'Jumu\u2018ah-Zeiten als HH:mm, mit Komma getrennt').optional(),
    hijriAdjust: z.number().int().min(-2).max(2).optional(),
    showHijri: z.boolean().optional(),
    showDate: z.boolean().optional(),
    showSunrise: z.boolean().optional(),
    showIqama: z.boolean().optional(),
    showCountdown: z.boolean().optional(),
    lang: z.enum(PRAYER_LANGS).optional(),
    view: z.enum(PRAYER_VIEWS).optional(),
    title: z.string().max(80).optional(),
    /** Hinweis während der Iqama, z. B. „Bitte Handy lautlos"; noticeText leer = Standardtext der Sprache. */
    notice: z.boolean().optional(),
    noticeText: z.string().max(120).optional(),
    noticeMinutes: z.number().int().min(1).max(60).optional(),
    /** Farbe der Hervorhebung (aktuelles Gebet, Countdown). */
    accent: color.optional(),
    font: FontFamilySchema.optional(),
});
/** Datenquelle (id aus „Datenquellen"); leer = noch nicht gewählt. */
const sourceRef = z.string().max(64).optional();
/** Newsticker/Feed aus einer RSS-/Atom-Quelle. */
export const FeedElementSchema = z.object({
    ...elBase,
    ...textish,
    ...typo,
    type: z.literal('feed'),
    source: sourceRef,
    /** ticker = Laufschrift, cards = ein Beitrag nach dem anderen, list = mehrere Beiträge untereinander. */
    mode: z.enum(['ticker', 'cards', 'list']).optional(),
    /** Anzahl Beiträge (1–20). */
    count: z.number().int().min(1).max(20).optional(),
    images: z.boolean().optional(),
    showDate: z.boolean().optional(),
    showText: z.boolean().optional(),
    /** Laufschrift: Sekunden je 120 Zeichen (mind. 5). */
    speed: z.number().optional(),
    /** Karten: Sekunden je Beitrag (mind. 3). */
    interval: z.number().optional(),
    accent: color.optional(),
    title: z.string().max(80).optional(),
});
/** Termine aus einem Kalender (iCal/ICS, Google Kalender). */
export const CalendarElementSchema = z.object({
    ...elBase,
    ...textish,
    type: z.literal('calendar'),
    source: sourceRef,
    /** today = heute, week = die nächsten 7 Tage. */
    range: z.enum(['today', 'week']).optional(),
    /** list = Liste, day = Tagesansicht mit Stundenraster (eine Spalte je Tag). */
    view: z.enum(['list', 'day']).optional(),
    title: z.string().max(80).optional(),
    accent: color.optional(),
    font: FontFamilySchema.optional(),
    showLocation: z.boolean().optional(),
    /** Höchstens so viele Termine (Liste). */
    maxItems: z.number().int().min(1).max(50).optional(),
});
/** Tabelle aus CSV/Excel oder Google Sheets. */
export const TableElementSchema = z.object({
    ...elBase,
    ...textish,
    type: z.literal('table'),
    source: sourceRef,
    /** Angezeigte Spalten (Schlüssel); leer = alle. */
    columns: z.array(z.string().max(64)).max(30).optional(),
    header: z.boolean().optional(),
    /** Zeilen pro Seite; mehr Zeilen werden durchgeblättert. */
    rowsPerPage: z.number().int().min(1).max(50).optional(),
    /** Sekunden pro Seite (mind. 3). */
    pageSeconds: z.number().optional(),
    stripes: z.boolean().optional(),
    /** Kopfzeile: Hintergrund. */
    accent: color.optional(),
    /** Kopfzeile: Schriftfarbe. */
    headColor: color.optional(),
    title: z.string().max(80).optional(),
    font: FontFamilySchema.optional(),
    /** Nur Zeilen für heute: Spalte mit Datum oder Wochentag. */
    today: z.string().max(64).optional(),
});
/** Feste Inhalte (Text, Bild, Bilderfolge) für die Felder einer eingebetteten Vorlage. */
export const EmbedContentSchema = z.union([
    z.object({ type: z.literal('text'), value: z.string().max(5000) }),
    z.object({ type: z.literal('media'), asset: sha256, fit: FitSchema.optional() }),
    z.object({
        type: z.literal('playlist'),
        items: z.array(z.object({ asset: sha256, durationMs: z.number().int().positive().optional() })).max(50),
        fit: FitSchema.optional(),
    }),
]);
/**
 * Vorlage als Bereich (Erweiterung „Vorlage einbetten"): zeigt eine veröffentlichte Vorlage (eigene oder globale)
 * in der Box, skaliert mit ihrem eigenen Seitenverhältnis. `content` füllt deren Felder wie ein Playlist-Eintrag.
 */
export const LayoutEmbedElementSchema = z.object({
    ...elBase,
    type: z.literal('layout'),
    /** id der eingebetteten Vorlage; leer = noch nicht gewählt. */
    layoutId: z.string().max(64).regex(/^[0-9a-f-]*$/i, 'Ungültige Vorlage').default(''),
    /** Ausrichtung der eingebetteten Vorlage (setzt der Server beim Speichern). */
    orientation: z.enum(['landscape', 'portrait']).optional(),
    /** contain = ganz sichtbar (Standard), cover = füllt die Box (Ränder abgeschnitten), fill = verzerrt. */
    fit: FitSchema.optional(),
    content: z.record(z.string().max(64), EmbedContentSchema).optional(),
});
export const ElementSchema = z.discriminatedUnion('type', [
    TextElementSchema,
    ImageElementSchema,
    SlideshowElementSchema,
    VideoElementSchema,
    ClockElementSchema,
    WeatherElementSchema,
    WebElementSchema,
    CameraElementSchema,
    TickerElementSchema,
    ScheduleElementSchema,
    PrayerElementSchema,
    FeedElementSchema,
    CalendarElementSchema,
    TableElementSchema,
    LayoutEmbedElementSchema,
]);
export const LayoutV2Schema = z.object({
    specVersion: z.literal(2),
    bg: color.optional(),
    bgSrc: mediaSrc.optional(),
    bgAsset: sha256.optional(),
    /** Grundschrift der Folie (gilt auch für Uhr und Wetter). */
    font: FontFamilySchema.optional(),
    elements: z.array(ElementSchema),
    /**
     * Ausfüllbare Felder (M16): `zone` ist die id eines Elements. Der Inhalt je Playlist-Eintrag
     * (gleiche Form wie bei specVersion 1) ersetzt Text, Bild, Bilderfolge oder Kursplan des Elements.
     */
    fields: z.array(FieldSchema).optional(),
});
export const LayoutSchema = z.discriminatedUnion('specVersion', [LayoutV1Schema, LayoutV2Schema]);
/**
 * Liest ein Layout; Prototyp-Folien ohne specVersion ({ bg, bgSrc, elements }) gelten als specVersion 2.
 */
export function parseLayout(raw) {
    if (raw && typeof raw === 'object' && !('specVersion' in raw) && 'elements' in raw)
        return LayoutSchema.parse({ ...raw, specVersion: 2 });
    return LayoutSchema.parse(raw);
}
export function safeParseLayout(raw) {
    try {
        return { success: true, data: parseLayout(raw) };
    }
    catch (e) {
        const issues = e.issues;
        return {
            success: false,
            error: issues ? issues.map((i) => `${i.path.map(String).join('.')}: ${i.message}`).join('; ') : String(e),
        };
    }
}
/** Ausfüllbare Ziele eines Layouts: Zonen (v1) bzw. Elemente mit id (v2), mit Position in Prozent. */
export function fillTargets(layout) {
    if (layout.specVersion === 1) {
        const W = layout.refWidth;
        const H = layout.refHeight;
        return layout.zones.map((z) => ({
            id: z.id,
            type: z.type,
            kind: z.type === 'text' ? 'text' : z.type === 'logo' ? 'logo' : z.type === 'media' ? (z.source === 'playlist' ? 'playlist' : 'media') : 'none',
            x: (z.x / W) * 100, y: (z.y / H) * 100, w: (z.w / W) * 100, h: (z.h / H) * 100,
        }));
    }
    return layout.elements.flatMap((e) => (e.id === undefined ? [] : [{
            id: e.id,
            type: e.type,
            kind: ELEMENT_FIELD_KIND[e.type] ?? 'none',
            x: e.x, y: e.y, w: e.w, h: e.h,
        }]));
}
const ELEMENT_FIELD_KIND = {
    text: 'text', ticker: 'text', image: 'media', video: 'media', slideshow: 'playlist', schedule: 'rows',
    feed: 'data', calendar: 'data', table: 'data',
};
/**
 * Prüft Zoneninhalte gegen die Felder des Layouts (Kap. 6.3: fehlende Pflichtfelder → 422).
 * specVersion 2: Felder verweisen auf Element-ids; Kursplan-Inhalt ist Text (eine Zeile je Kurs).
 */
export function validateContent(layout, content) {
    const out = [];
    const zones = new Map(fillTargets(layout).map((z) => [z.id, z]));
    for (const f of layout.fields ?? []) {
        const c = content[f.zone];
        if (!c) {
            if (f.required)
                out.push({ zone: f.zone, message: 'Pflichtfeld fehlt' });
            continue;
        }
        if (c.type === 'text' && f.maxLength !== undefined && c.value.length > f.maxLength)
            out.push({ zone: f.zone, message: `Höchstens ${f.maxLength} Zeichen` });
        if (c.type === 'text' && f.required && !c.value.trim())
            out.push({ zone: f.zone, message: 'Pflichtfeld fehlt' });
    }
    for (const [id, c] of Object.entries(content)) {
        const z = zones.get(id);
        if (!z)
            out.push({ zone: id, message: 'Unbekannte Zone' });
        else if ((z.kind === 'text' || z.kind === 'rows' || z.kind === 'data') && c.type !== 'text' && c.type !== 'data')
            out.push({ zone: id, message: 'Textzone erwartet Text' });
        else if ((z.kind === 'media' || z.kind === 'playlist') && (c.type === 'text' || c.type === 'data'))
            out.push({ zone: id, message: 'Medienzone erwartet Bild oder Video' });
        else if (z.kind === 'none' && layout.specVersion === 2)
            out.push({ zone: id, message: 'Element ist nicht ausfüllbar' });
    }
    return out;
}
/** ids der direkt eingebetteten Vorlagen (Elemente vom Typ "layout"), ohne Doppelte. */
export function embeddedLayoutIds(def) {
    const els = def?.elements;
    if (!Array.isArray(els))
        return [];
    const out = new Set();
    for (const e of els) {
        if (e && e.type === 'layout' && typeof e.layoutId === 'string' && e.layoutId)
            out.add(e.layoutId);
    }
    return [...out];
}
//# sourceMappingURL=schema.js.map